import * as React from "react";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import { UPDATE_PLANNING_TABLE_WIDTH } from "../reducers/gantt/properties";
import {
    GanttChartSource,
    zoomGanttChart,
    getCustomDayWidth,
    getZoomPersistenceSourceType,
} from "../reducers/gantt/properties";
import { SET_IS_CTRL_DOWN } from "../reducers/planningTable";
import { PLANNING_TABLE_DISPLAY_TYPES } from "../toolbar/DisplayTypesMenu";
import { DraggableLogic, PropertiesLogic } from "../reducers/logic/businessLogic";
import { AutomaticScroll } from "./AutomaticScroll";
import { HEADER_HEIGHT } from "../../viewsCommon";
import ResizableHandle from "./ResizableHandle";

import type { ActionTypes, ReduxState } from "../reducers/reducers";
import type { PlanningDetailsPosition } from "../../viewsCommon";

type OwnProps = {
    show_planning_details_filter?: boolean;
    selected_display_type?: keyof typeof PLANNING_TABLE_DISPLAY_TYPES;
    planning_details_position?: PlanningDetailsPosition;
    planning_details_size_ratio?: number;
    children?: React.ReactNode;
};

type Props = OwnProps & ReduxProps;

class Controls extends React.Component<Props> {
    key_down_event_listener: ((e: KeyboardEvent) => void) | null = null;
    key_up_event_listener: ((e: KeyboardEvent) => void) | null = null;
    wheel_event_listener: ((e: WheelEvent) => void) | null = null;
    scroll_event_listener: ((e: Event) => void) | null = null;
    scroll_x_event_listener: ((e: Event) => void) | null = null;

    min_width: number | null = null;
    max_width: number | null = null;
    controls_ref = React.createRef<HTMLDivElement>();
    scrollDiff = 0;

    componentDidMount() {
        if (PropertiesLogic.isPlanningTable(this.props.source_type)) {
            return;
        }
        // we cannot use react onKeyDown beacuse this requires the div to be in focus (tabIndex)
        if (!this.key_down_event_listener) {
            this.key_down_event_listener = e => this.onKeyDown(e);
        }
        window.addEventListener("keydown", this.key_down_event_listener);

        if (!this.key_up_event_listener) {
            this.key_up_event_listener = () => this.onKeyUp();
        }
        window.addEventListener("keyup", this.key_up_event_listener);

        if (!this.wheel_event_listener) {
            this.wheel_event_listener = e => this.onWheel(e);
        }
        window.addEventListener("wheel", this.wheel_event_listener, { passive: false });

        this.addScrollEventListener();

        if (this.controls_ref && this.controls_ref.current) {
            this.min_width = this.controls_ref.current.getBoundingClientRect().width;
            this.max_width = this.min_width * 10;
        }

        this.setScrollDiff();
    }

    componentDidUpdate(prev_props: Props) {
        if (
            prev_props.show_planning_details_filter !== this.props.show_planning_details_filter &&
            this.controls_ref &&
            this.controls_ref.current
        ) {
            this.min_width = this.controls_ref.current.getBoundingClientRect().width;
            this.max_width = this.min_width * 10;
            this.onReset();
        }

        if (prev_props.source_type !== this.props.source_type) {
            this.addScrollEventListener();
        }

        if (prev_props.height !== this.props.height) {
            const el = DraggableLogic.querySelector(this.props.source_type, "#gantt-chart-container");
            if (el) {
                el.dispatchEvent(new Event("scroll"));
            }
        }

        this.setScrollDiff();
    }

    componentWillUnmount() {
        if (this.key_down_event_listener) {
            window.removeEventListener("keydown", this.key_down_event_listener);
        }
        if (this.key_up_event_listener) {
            window.removeEventListener("keyup", this.key_up_event_listener);
        }
        if (this.wheel_event_listener) {
            window.removeEventListener("wheel", this.wheel_event_listener);
        }
        if (this.scroll_event_listener) {
            window.removeEventListener("scroll", this.scroll_event_listener);
        }

        const scroll_x_event_listener = this.scroll_x_event_listener;
        if (scroll_x_event_listener !== null) {
            const chart_wrapper = DraggableLogic.querySelector(
                this.props.source_type,
                "#gantt-chart-container > .chart-wrapper"
            );
            if (chart_wrapper !== null) {
                chart_wrapper.removeEventListener("scroll", scroll_x_event_listener);
            }
        }
    }

    addScrollEventListener = () => {
        if (!PropertiesLogic.isPlanningTable(this.props.source_type) && !this.scroll_event_listener) {
            // on planning table scroll is done inside main div in render (onScroll)
            // on gantt chart the scroll is done on the window
            if (!this.scroll_event_listener) {
                this.scroll_event_listener = e => this.onScroll(e);
            }

            window.addEventListener("scroll", this.scroll_event_listener, { passive: false });

            const chart_wrapper = DraggableLogic.querySelector(
                this.props.source_type,
                "#gantt-chart-container > .chart-wrapper"
            );
            if (chart_wrapper !== null) {
                if (this.scroll_x_event_listener === null) {
                    this.scroll_x_event_listener = (e: Event) => this.onScrollX(e);
                }

                chart_wrapper.addEventListener("scroll", this.scroll_x_event_listener, { passive: false });
            }
        }
    };

    setScrollDiff = () => {
        const source_type = this.props.source_type;
        const el1 = DraggableLogic.querySelector(source_type, "#gantt-chart-container");
        const el2 =
            document.querySelector(".planning-table-app-menu") ||
            document.querySelector(".views-filter-bar") ||
            document.querySelector(".navigation-bar");

        if (el1 && el2) {
            const rect1 = el1.getBoundingClientRect();
            const rect2 = el2.getBoundingClientRect();
            this.scrollDiff = rect1.top - (rect2.top + rect2.height) + window.scrollY;
        }
    };

    onKeyUp = () => {
        if (this.props.is_ctrl_down) {
            this.props.reduxDispatch({ type: SET_IS_CTRL_DOWN, data: false });
        }
    };

    onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Meta" || e.key === "Control") {
            if (!this.props.is_ctrl_down) {
                this.props.reduxDispatch({ type: SET_IS_CTRL_DOWN, data: true });
            }
            e.preventDefault();
            e.stopPropagation();
        }
    };

    onMouseLeave = () => {
        if (this.props.is_ctrl_down) {
            this.props.reduxDispatch({ type: SET_IS_CTRL_DOWN, data: false });
        }
    };

    zoom_gantt_timeout: ReturnType<typeof setTimeout> | null = null;

    onWheel = (e: WheelEvent) => {
        if (this.props.disable_zoom) {
            return false;
        }

        if (!this.props.is_ctrl_down || PropertiesLogic.isPlanningTable(this.props.source_type)) {
            e.stopPropagation();
            return null;
        }

        e.stopPropagation();
        e.preventDefault();

        const ZOOM_STEP = this.props.min_day_width / 5;
        const width = this.props.width;
        const custom_day_width = getCustomDayWidth(getZoomPersistenceSourceType(this.props.source_type));

        if (e.deltaY > 0) {
            if (this.min_width && width && width - ZOOM_STEP > this.min_width) {
                if (this.zoom_gantt_timeout) {
                    clearTimeout(this.zoom_gantt_timeout);
                }
                this.zoom_gantt_timeout = setTimeout(() => {
                    zoomGanttChart(custom_day_width - ZOOM_STEP, this.props.source_type, this.props.reduxDispatch);
                }, 200);
            }
        } else {
            if (this.min_width !== null && width && width + ZOOM_STEP < (this.max_width ?? 0)) {
                if (this.zoom_gantt_timeout) {
                    clearTimeout(this.zoom_gantt_timeout);
                }
                this.zoom_gantt_timeout = setTimeout(() => {
                    zoomGanttChart(custom_day_width + ZOOM_STEP, this.props.source_type, this.props.reduxDispatch);
                }, 200);
            }
        }
    };

    onScroll = (event: React.SyntheticEvent<HTMLElement> | Event) => {
        let source_type = this.props.source_type;
        if (source_type === GanttChartSource.microplan || PropertiesLogic.isPlanningTable(source_type)) {
            return;
        }

        let header_shifts_group = DraggableLogic.querySelector(source_type, "#grid-groups-header-shifts");

        if (this.props.is_ctrl_down) {
            event.stopPropagation();
            event.preventDefault();
            return null;
        }
        if (!event.currentTarget) return;

        const correction = 0;
        const scroll_top =
            event.currentTarget instanceof Window
                ? event.currentTarget.scrollY - correction
                : event.currentTarget instanceof HTMLElement
                  ? event.currentTarget.scrollTop
                  : 0;

        let y = scroll_top - this.scrollDiff;
        if (y < 0) y = 0;

        const translate_y = `translate(0, ${y})`;

        if (header_shifts_group) {
            header_shifts_group.setAttribute("transform", translate_y);
        }

        const scroll_x =
            event.currentTarget instanceof Window
                ? event.currentTarget.scrollX
                : event.currentTarget instanceof HTMLElement
                  ? event.currentTarget.scrollLeft
                  : 0;

        let x = scroll_x - 10;

        if (x < 0) x = 0;
    };

    onScrollX = (event: Event) => {
        if (event.currentTarget instanceof HTMLElement) {
            const scroll_x = event.currentTarget.scrollLeft;
            const translate_x = `translate(${scroll_x}, 0)`;

            let line_labels_group = DraggableLogic.querySelector(this.props.source_type, "#line-labels-group");
            if (line_labels_group) {
                line_labels_group.setAttribute("transform", translate_x);
            }
        }
    };

    onReset = () => {
        if (this.min_width !== null) {
            this.props.reduxDispatch({
                type: UPDATE_PLANNING_TABLE_WIDTH,
                data: undefined,
            });
        }
    };

    dynamic_props: { onScroll?: (event: React.SyntheticEvent<HTMLElement> | Event) => void } = {};

    getDynamicProps = () => {
        if (!this.dynamic_props.onScroll) {
            this.dynamic_props.onScroll = this.onScroll;
        }
        return this.dynamic_props;
    };

    getControlsContainerStyle = () => {
        const {
            show_planning_details_filter,
            planning_details_size_ratio = 0,
            planning_details_position,
            source_type,
        } = this.props;

        let spacing = 0;
        let left_offset = spacing;
        let gantt_height: string | number = "100%";

        if (show_planning_details_filter) {
            spacing = ResizableHandle.WIDTH;
            left_offset = planning_details_size_ratio;
            gantt_height = (window.innerHeight - HEADER_HEIGHT) * (1 - planning_details_size_ratio);
        }

        return planning_details_position === "left"
            ? {
                  left: `${left_offset * 100}%`,
                  width: `${(1 - left_offset) * 100}%`,
                  height: PropertiesLogic.isPlanningTable(source_type) ? `calc(100vh - ${HEADER_HEIGHT}px)` : "unset",
              }
            : {
                  left: 0,
                  width: "100%",
                  height: gantt_height,
              };
    };

    getGanttChartContainerStyle = () => {
        const style: React.CSSProperties = {
            paddingTop: PropertiesLogic.isPlanningTable(this.props.source_type) ? 0 : 15,
            position: undefined,
            width: undefined,
        };

        if (this.props.source_type === GanttChartSource.tool_changes) {
            style.paddingTop = 0;
        }
        if (this.props.source_type === GanttChartSource.line_planning) {
            style.position = "unset";
        }

        return style;
    };

    getShowToolbar = () => {
        return (
            !this.props.disable_zoom &&
            (this.props.selected_display_type === PLANNING_TABLE_DISPLAY_TYPES.gantt ||
                this.props.source_type === GanttChartSource.gantt ||
                this.props.source_type == GanttChartSource.microplan ||
                this.props.source_type == GanttChartSource.line_planning)
        );
    };

    getSourceType = () => {
        return this.props.source_type;
    };

    render() {
        const classes = PropertiesLogic.isPlanningTable(this.props.source_type) ? "" : "overflow-hidden";
        return (
            <div
                onMouseLeave={this.onMouseLeave}
                id={this.getSourceType()}
                className={`position-relative ${classes}`}
                style={this.getControlsContainerStyle()}
            >
                <AutomaticScroll bar_height={this.props.bar_height} />
                <div
                    id="gantt-chart-container"
                    className="planning-table-main2 h-100"
                    ref={this.controls_ref}
                    style={this.getGanttChartContainerStyle()}
                    {...this.getDynamicProps()}
                >
                    {this.props.children}
                </div>
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const is_ctrl_down = state.gantt_chart_planning_table.is_ctrl_down;
    const properties = state.gantt_chart_properties;

    return {
        width: properties.width,
        height: properties.height,
        source_type: properties.source_type,
        min_day_width: properties.min_day_width,
        bar_height: properties.bar_height,
        disable_zoom: properties.disable_zoom,
        is_ctrl_down,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(Controls);
